<script setup>
import { ref, computed, useSlots } from 'vue';
import { trans } from '@elements/translation';
import Dropdown from '@molecules/components/Dropdown.vue';

const emit = defineEmits([ 'row-click' ]);

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  data: {
    type: Array,
    required: true,
  },
  activateInsideLines: {
    type: Boolean,
    default: false,
  },
  clickable: {
    type: Boolean,
    default: false,
  },
});
const slots = useSlots();
const sortKey = ref(null);
const sortDirection = ref('asc');

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    return;
  }

  sortKey.value = key;
  sortDirection.value = 'asc';
};

const isDate = (value) => !Number.isNaN(Date.parse(value));

const sortedData = computed(() => {
  const key = sortKey.value;
  const dir = sortDirection.value === 'asc' ? 1 : -1;

  if (!key) {
    return props.data;
  }

  const header = props.headers.find((h) => h.key === key);

  if (!header) {
    return props.data;
  }

  const parseInvoiceNumber = (val) => {
    const [ date, num ] = (val || '').split('-');

    return [ parseInt(date, 10) || 0, parseInt(num, 10) || 0 ];
  };

  const normalize = (val) => {
    if (header.transPrefix) {
      return trans(`${header.transPrefix}${val}`);
    }

    if (isDate(val)) {
      return new Date(val).getTime();
    }

    if (!Number.isNaN(val)) {
      return parseFloat(val);
    }

    return val?.toString() || '';
  };

  return [ ...props.data ].sort((a, b) => {
    if (key === 'number') {
      const [ aDate, aNum ] = parseInvoiceNumber(a[key]);
      const [ bDate, bNum ] = parseInvoiceNumber(b[key]);

      return (aDate - bDate || aNum - bNum) * dir;
    }

    const aVal = normalize(a[key]);
    const bVal = normalize(b[key]);

    if (aVal > bVal) {
      return dir;
    }

    if (aVal < bVal) {
      return -dir;
    }

    return 0;
  });
});

const getValue = (row, header) => {
  if (header.ignoredTrans && header.ignoredTrans.includes(trans(`${header.transPrefix}${row[header.key]}`))) {
    return '';
  }

  if (!header.transPrefix) {
    return row[header.key];
  }

  return trans(`${header.transPrefix}${row[header.key]}`);
};

const getAdditionalClass = (header, row) => {
  if (header.key === 'commission_type') {
    return `commission-type commission-type--${row[header.key]}`;
  }

  if (header.key === 'type') {
    return `type type--${row[header.key]}`;
  }

  if (header.key !== 'status' && header.key !== 'active_subscription') {
    return '';
  }

  return `status-label status-label--${row[header.key]}`;
};

const getCustomClassDropdown = (line, arraySize) => {
  if (!props.activateInsideLines) {
    return '';
  }
  if (arraySize === 1 || line === arraySize - 2) {
    return 'center';
  }

  return line === arraySize - 1 ? 'end' : 'start';
};

const hasSlot = (slot) => !!slots[slot];

const rowClick = (row) => {
  if (!props.clickable) {
    return;
  }
  emit('row-click', row);
};
</script>

<template>
  <div class="simple-table-wrap">
    <table class="simple-table">
      <thead>
        <tr>
          <th
            v-for="header in headers"
            :key="header.key"
            class="simple-table__header"
            :class="header.thClass"
            @click="header.sort ? sortBy(header.key) : null"
          >
            {{ header.title }}
            <span v-if="header.sort">
              <template v-if="sortKey === header.key">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </template>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, i) in sortedData"
          :key="row.id"
          class="simple-table__row"
          :class="[ clickable ? 'simple-table__row--clickable' : '' ]"
          @click="rowClick(row)"
        >
          <td
            v-for="header in headers"
            :key="header.key"
            :class="[
              `simple-table__column--${header.key}`,
              header.specialClass ? `simple-table__column--${header.specialClass}` : ''
            ]"
          >
            <div v-if="header.key === 'action' && row.actions" class="simple-table__column--actions">
              <Dropdown
                :options="row.actions"
                :custom-class="getCustomClassDropdown(i, sortedData.length)"
              />
            </div>
            <template v-else-if="hasSlot(header.key)">
              <slot :name="header.key" :row="row" :header="header" />
            </template>
            <span v-else :class="getAdditionalClass(header, row)">
              {{ getValue(row, header) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
